import { useState, useEffect, useMemo } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Buildings, CheckCircle } from '@phosphor-icons/react';

const GRUPOS = [
  { titulo: 'Trabajo diario', mods: [
    ['mi_dia', 'Mi Día'], ['cotizador', 'Cotizador'], ['pendientes', 'Pendientes'], ['chat_interno', 'Chat interno'],
  ]},
  { titulo: 'Mensajes y redes sociales', mods: [
    ['inbox', 'Mensajes (todos)'], ['inbox_asignado', 'Mensajes (solo asignados)'], ['dashboard', 'Dashboard mensajes'],
    ['orders', 'Pedidos'], ['customers', 'Clientes de redes'], ['clientes_redes', 'Directorio clientes redes'],
    ['wa_campaigns', 'Campañas WhatsApp'], ['templates', 'Plantillas de respuesta'],
  ]},
  { titulo: 'Ventas y clientes', mods: [
    ['clientes_vendedores', 'Clientes de vendedores'], ['plan_trabajo', 'Plan de trabajo'], ['ranking', 'Ranking de vendedores'],
    ['vendors', 'Vendedores'], ['coverage', 'Cobertura territorial'], ['reportes', 'Reportes semanales'],
    ['reports', 'Reportes clásicos'], ['prospectos', 'Prospectos IA'], ['dashboard_comercial', 'Dashboard comercial'],
  ]},
  { titulo: 'Producción y catálogo', mods: [
    ['produccion', 'Producción (órdenes de trabajo)'], ['programacion', 'Programación semanal'],
    ['instaladores', 'Instaladores'], ['presupuestos_sa', 'Presupuestos SA'], ['catalogo', 'Catálogo'],
  ]},
  { titulo: 'Inteligencia IA', mods: [
    ['alertas', 'Alertas IA'], ['asistente_ia', 'Asistente IA'], ['chat_ia', 'Chat IA con datos'], ['ai_costos', 'Costos IA'],
  ]},
  { titulo: 'Administración', mods: [
    ['harperp', 'HarpERP'], ['landing_pages', 'Landing Pages'], ['leads_landing', 'Leads Landing'],
    ['tienda_admin', 'Tienda en línea (admin)'], ['dashboard_gerencial', 'Dashboard gerencial'], ['settings', 'Configuración'],
  ]},
];

const TODAS_LABELS = Object.fromEntries(GRUPOS.flatMap((g) => g.mods));

const disponibleEn = (mod, bizName) => {
  const n = (bizName || '').toLowerCase();
  if (mod === 'presupuestos_sa') return n.includes('soluciones');
  if (mod === 'tienda_admin') return n.includes('complementos');
  return true;
};

export const ConfigurarAcceso = ({ user, businesses, open, onClose, onSaved, allUsers = [] }) => {
  const [form, setForm] = useState({});
  const [tabPerms, setTabPerms] = useState({});
  const [saving, setSaving] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [historial, setHistorial] = useState(null);
  const [showHist, setShowHist] = useState(false);

  useEffect(() => {
    if (!user) return;
    const f = {}, tp = {};
    (user.assigned_permissions || []).forEach((ap) => {
      f[ap.business_id] = [...(ap.permissions || [])];
      tp[ap.business_id] = ap.tab_permissions || {};
    });
    setForm(f);
    setTabPerms(tp);
    setBusqueda('');
    setHistorial(null);
    setShowHist(false);
  }, [user]);

  const copiarDe = (uid) => {
    const src = allUsers.find((u) => u.id === uid);
    if (!src) return;
    const f = {}, tp = {};
    (src.assigned_permissions || []).forEach((ap) => {
      f[ap.business_id] = [...(ap.permissions || [])];
      tp[ap.business_id] = ap.tab_permissions || {};
    });
    setForm(f);
    setTabPerms(tp);
    toast.info(`Acceso copiado de ${src.name} — revisa y guarda`);
  };

  const cargarHistorial = async () => {
    if (showHist) { setShowHist(false); return; }
    setShowHist(true);
    if (historial) return;
    try {
      const r = await api.get('/admin/activity-log');
      const rows = (r.data || []).filter((l) =>
        l.action === 'permisos_actualizados' && (l.detail || '').includes(user.name));
      setHistorial(rows.slice(0, 15));
    } catch { setHistorial([]); }
  };

  const empresasActivas = Object.keys(form);
  const resumenModulos = useMemo(() => {
    const s = new Set();
    Object.values(form).forEach((perms) => perms.forEach((p) => s.add(p)));
    return [...s].map((p) => TODAS_LABELS[p] || p);
  }, [form]);

  if (!user) return null;

  const toggleEmpresa = (biz) => {
    setForm((prev) => {
      const n = { ...prev };
      if (n[biz.id]) delete n[biz.id];
      else n[biz.id] = ['mi_dia'];
      return n;
    });
  };

  const toggleModulo = (bizId, mod) => {
    setForm((prev) => {
      const perms = prev[bizId] || [];
      return { ...prev, [bizId]: perms.includes(mod) ? perms.filter((p) => p !== mod) : [...perms, mod] };
    });
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const antes = new Set((user.assigned_permissions || []).map((ap) => ap.business_id));
      for (const bizId of Object.keys(form)) {
        await api.put(`/admin/users/${user.id}/permissions`, {
          user_id: user.id, business_id: bizId, permissions: form[bizId], tab_permissions: tabPerms[bizId] || {},
        });
        antes.delete(bizId);
      }
      for (const bizId of antes) {
        await api.delete(`/admin/users/${user.id}/permissions/${bizId}`);
      }
      toast.success(`Acceso de ${user.name} actualizado`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error('No se pudo guardar el acceso');
    } finally {
      setSaving(false);
    }
  };

  const q = busqueda.trim().toLowerCase();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="configurar-acceso-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">¿Qué puede hacer {user.name}?</DialogTitle>
          <DialogDescription>
            Perfil: <b className="text-[var(--text)]">{user.role}</b> — el perfil es solo la base; aquí decides exactamente a qué empresas entra y qué módulos usa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <select defaultValue="" onChange={(e) => { if (e.target.value) { copiarDe(e.target.value); e.target.value = ''; } }}
              className="h-9 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-2"
              data-testid="acceso-copiar-de">
              <option value="">⚡ Copiar acceso de otro usuario…</option>
              {allUsers.filter((u) => u.id !== user.id && u.role !== 'admin' && (u.assigned_permissions || []).length > 0).map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({(u.assigned_permissions || []).length} empresa{(u.assigned_permissions || []).length !== 1 ? 's' : ''})</option>
              ))}
            </select>
            <button type="button" onClick={cargarHistorial}
              className="h-9 px-3 text-xs font-bold border border-[var(--border)] rounded-md text-[var(--text-muted)] hover:border-[#1D4ED8]/50"
              data-testid="acceso-historial-btn">
              🕘 {showHist ? 'Ocultar historial' : 'Historial de cambios'}
            </button>
          </div>

          {showHist && (
            <div className="border border-[var(--border)] rounded-xl p-3 max-h-44 overflow-y-auto" data-testid="acceso-historial">
              {historial === null ? <p className="text-xs text-[var(--text-muted)]">Cargando…</p>
                : historial.length === 0 ? <p className="text-xs text-[var(--text-faint)]">Sin cambios registrados para este usuario.</p>
                : historial.map((h, i) => (
                  <div key={i} className="py-1.5 border-b border-[var(--border)] last:border-0">
                    <p className="text-[11px] text-[var(--text)]">{h.detail}</p>
                    <p className="text-[10px] text-[var(--text-muted)]">Por <b>{h.user_name || h.user_id}</b> · {h.timestamp ? new Date(h.timestamp).toLocaleString('es-GT') : ''}</p>
                  </div>
                ))}
            </div>
          )}

          <div>
            <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)] mb-2">1 · Empresas / Negocios</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {businesses.map((b) => {
                const activa = !!form[b.id];
                return (
                  <button key={b.id} type="button" onClick={() => toggleEmpresa(b)} data-testid={`acceso-biz-${b.id}`}
                    className={`flex items-center gap-2 border-2 rounded-xl p-3 text-left transition-colors ${activa ? 'border-[#1D4ED8] bg-[#1D4ED8]/10' : 'border-[var(--border)] hover:border-[#1D4ED8]/50'}`}>
                    <Buildings size={18} weight="duotone" className={activa ? 'text-[#1D4ED8]' : 'text-[var(--text-muted)]'} />
                    <span className="text-sm font-bold text-[var(--text)] flex-1">{b.name}</span>
                    {activa && <CheckCircle size={18} weight="fill" className="text-[#1D4ED8]" />}
                  </button>
                );
              })}
            </div>
          </div>

          {empresasActivas.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">2 · Módulos por empresa</p>
                <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar módulo…"
                  className="h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-2 w-44" data-testid="acceso-buscar-modulo" />
              </div>
              <div className="space-y-3">
                {empresasActivas.map((bizId) => {
                  const biz = businesses.find((b) => b.id === bizId);
                  const perms = form[bizId] || [];
                  return (
                    <div key={bizId} className="border border-[var(--border)] rounded-xl p-3" data-testid={`acceso-mods-${bizId}`}>
                      <p className="text-sm font-black text-[#1D4ED8] mb-2">{biz?.name || bizId} <span className="text-[var(--text-muted)] font-bold text-xs">· {perms.length} módulo(s) permitido(s)</span></p>
                      <div className="space-y-2">
                        {GRUPOS.map((g) => {
                          const mods = g.mods.filter(([m, lbl]) => disponibleEn(m, biz?.name) && (!q || lbl.toLowerCase().includes(q)));
                          if (!mods.length) return null;
                          return (
                            <div key={g.titulo}>
                              <p className="text-[10px] font-black uppercase text-[var(--text-muted)]">{g.titulo}</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-1">
                                {mods.map(([m, lbl]) => (
                                  <label key={m} className="flex items-center justify-between gap-2 py-0.5 cursor-pointer">
                                    <span className="text-xs text-[var(--text)]">{lbl}</span>
                                    <span className="flex items-center gap-1.5">
                                      <span className={`text-[9px] font-bold ${perms.includes(m) ? 'text-emerald-600' : 'text-[var(--text-faint)]'}`}>
                                        {perms.includes(m) ? 'Permitido' : 'No permitido'}
                                      </span>
                                      <Switch checked={perms.includes(m)} onCheckedChange={() => toggleModulo(bizId, m)} data-testid={`acceso-switch-${bizId}-${m}`} />
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border border-[#1D4ED8]/40 bg-[#1D4ED8]/5 rounded-xl p-3" data-testid="acceso-resumen">
            <p className="text-xs font-black uppercase tracking-wide text-[#1D4ED8] mb-1">Acceso de este usuario</p>
            <p className="text-xs text-[var(--text)]"><b>Empresas:</b> {empresasActivas.length
              ? empresasActivas.map((id) => businesses.find((b) => b.id === id)?.name || id).join(' · ')
              : 'Ninguna — no podrá ver ningún negocio'}</p>
            <p className="text-xs text-[var(--text)] mt-1"><b>Módulos:</b> {resumenModulos.length ? resumenModulos.join(' · ') : 'Ninguno'}</p>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button onClick={guardar} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold" data-testid="acceso-guardar">
              {saving ? 'Guardando…' : 'Guardar acceso'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigurarAcceso;
